import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  DocumentTextIcon,
  ScaleIcon,
} from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import { loadCourtSessions } from '../../utils/courtSessions';
import { getGreeting } from '../../utils/registrarDashboard';

const SESSION_STATUS = {
  pending: {
    label: 'Pending',
    className: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  uploaded: {
    label: 'Uploaded',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
};

const KpiCard = ({ icon: Icon, title, value, sub, meta, trend, fill = 'primary' }) => {
  const fillClass = {
    primary: 'md-card-kpi-primary',
    dark: 'md-card-kpi-dark',
    warm: 'md-card-kpi-warm',
    slate: 'md-card-kpi-slate',
  }[fill] || 'md-card-kpi-primary';

  return (
    <div className={`md-card p-5 flex gap-4 items-start h-full ${fillClass}`}>
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center bg-white/15 shadow-[inset_2px_2px_6px_rgba(0,0,0,0.2),inset_-2px_-2px_6px_rgba(255,255,255,0.12)]">
        {Icon && <Icon className="h-6 w-6 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-1">{title}</p>
        <p className="text-2xl font-bold leading-none text-white">{value}</p>
        {sub && <p className="text-xs mt-1.5 text-white/70">{sub}</p>}
        {trend && <p className="text-xs mt-2 font-medium text-white/90">{trend}</p>}
        {meta && !trend && <p className="text-xs mt-2 text-white/65">{meta}</p>}
      </div>
    </div>
  );
};

const IconAction = ({ label, dark, children, to, onClick }) => {
  const className = `inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
    dark
      ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
      : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-matdash-primary'
  }`;
  if (to) {
    return (
      <Link to={to} title={label} aria-label={label} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} className={className}>
      {children}
    </button>
  );
};

const CourtAdminDashboard = () => {
  const { user } = useAuth();
  const { dark } = useTheme();
  const tenantId = user?.tenant_id || null;
  const [tenant, setTenant] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantActive, setTenantActive] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!tenantId) {
        setLoading(false);
        return;
      }
      try {
        const [tenantResp, activeResp, sessionRows] = await Promise.all([
          adminAPI.getTenant(tenantId).catch(() => null),
          adminAPI.isTenantActive(tenantId).catch(() => null),
          loadCourtSessions(tenantId).catch(() => []),
        ]);
        setTenant(tenantResp?.data?.data || tenantResp?.data || null);
        setTenantActive(activeResp?.data?.data?.active !== false);
        setSessions(sessionRows);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId]);

  const courtName = tenant?.name || user?.court_name || 'Court';
  const greeting = getGreeting();
  const pendingTranscriptions = sessions.filter((s) => s.status === 'pending').length;
  const totalSessions = sessions.length;
  const recentSessions = sessions.slice(0, 8);

  const card = `md-card ${dark ? '!bg-gray-900/95 !border-gray-800' : ''}`;
  const th = `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${
    dark ? 'text-gray-400' : 'text-matdash-muted'
  }`;
  const td = `px-4 py-3 text-sm ${dark ? 'text-gray-200' : 'text-matdash-dark'}`;
  const tdMuted = `px-4 py-3 text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className="space-y-6">
      {!tenantActive && (
        <div className="md-card md-card-body bg-matdash-danger-subtle border border-matdash-danger/20 text-sm text-red-800">
          Your court tenant has been deactivated. New recordings and uploads are blocked. Contact
          your platform administrator.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 items-stretch">
        <div className={`md-card md-card-primary overflow-hidden xl:col-span-2 ${dark ? '!border-gray-800' : ''}`}>
          <div className="md-card-body p-6 flex items-center gap-4 h-full min-h-[148px]">
            <div className="md-icon-well md-icon-well-lg bg-matdash-primary-subtle text-matdash-primary flex items-center justify-center shrink-0">
              <ScaleIcon className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-matdash-muted mb-1">
                Court overview
              </p>
              <h5 className="text-matdash-dark text-lg font-semibold leading-snug mb-1">
                {greeting} {user?.first_name || 'Court Admin'}
              </h5>
              <p className="text-matdash-muted text-sm mb-0">
                {courtName} — AI-powered court recording and transcription for your court.
              </p>
            </div>
          </div>
        </div>

        <KpiCard
          icon={DocumentTextIcon}
          fill="primary"
          title="Total Court Sessions"
          value={loading ? '…' : totalSessions}
          sub="Recorded hearings"
        />
        <KpiCard
          icon={ClockIcon}
          fill="dark"
          title="Pending Transcriptions"
          value={loading ? '…' : pendingTranscriptions}
          sub="In Queue"
        />
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-matdash-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-matdash-primary" />
            </span>
            <h2 className={`text-sm font-semibold truncate ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
              Live & Recent Court Recording Activity
            </h2>
          </div>
          <Link to="/admin/sessions" className="text-xs font-medium text-matdash-primary hover:underline whitespace-nowrap">
            View All Sessions →
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className={dark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={th}>Session ID</th>
                <th className={th}>Case Number</th>
                <th className={th}>Case Title</th>
                <th className={th}>Recording Date / Time</th>
                <th className={th}>Audio Duration</th>
                <th className={th}>Status</th>
                <th className={th}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${dark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {recentSessions.length === 0 && (
                <tr>
                  <td colSpan={7} className={`${tdMuted} text-center py-8`}>
                    No court sessions yet.
                  </td>
                </tr>
              )}
              {recentSessions.map((row) => {
                const status = SESSION_STATUS[row.status] || SESSION_STATUS.pending;
                const isUploaded = row.status === 'uploaded';
                const transcriptId = row.transcription_id;
                return (
                  <tr key={row.id} className={`transition-colors ${dark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                    <td className={`${td} font-semibold whitespace-nowrap`}>{row.session_id}</td>
                    <td className={`${tdMuted} whitespace-nowrap`}>{row.case_number}</td>
                    <td className={`${td} min-w-[220px]`}>{row.case_title}</td>
                    <td className={`${tdMuted} whitespace-nowrap`}>{row.recorded_at}</td>
                    <td className={`${tdMuted} whitespace-nowrap font-medium`}>{row.duration}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {isUploaded && transcriptId && (
                          <IconAction
                            label="View transcript"
                            dark={dark}
                            to={`/admin/transcriptions/${transcriptId}`}
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </IconAction>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t text-xs ${
          dark ? 'border-gray-800 text-gray-500' : 'border-matdash-border text-gray-400'
        }`}>
          <span>Showing {recentSessions.length} of {totalSessions} sessions</span>
          <Link to="/admin/sessions" className="text-xs font-medium text-matdash-primary hover:underline">
            View All Sessions →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CourtAdminDashboard;
